// src/tenantAdmin/components/SeatTile.jsx
//
// The COMPANY's people against its ceiling, exactly as the user-list envelope
// reports them. Markup lifted unchanged from the Utenti page.
//
// The three answers the envelope can give, kept apart on purpose:
//   • `used == null` → UNKNOWN (no customer, no plan, or the lookup failed): no
//     tile at all, because a wrong figure is worse than none.
//   • `source === 'unlimited'` → a figure with no ceiling.
//   • an integer `limit` → a ceiling, and `atCap` once it is met.
//
// ⛔⛔ This tile WARNS and nothing else — it never disables anything. The add-user
// dialog serves TWO actions behind one button: a NEW address (which the backend
// refuses at the cap, 409 USER_SEAT_LIMIT_REACHED) and an EXISTING colleague of
// the company (which it accepts EVEN WHEN FULL, because that person already holds
// their seat). Greying the button would block the second, so the tile warns and
// the 409 inside the dialog decides.
//
// ⛔ `showBillingLink` exists because the plans page acts on the company that owns
// the ACTIVE workspace. Looking at another administered workspace, that link may
// point at a different company's plan — so the remedy sentence stays (it also names
// deactivating someone) and only the link is withheld. The caller decides: it also
// withholds the link from a plain workspace admin, for whom the standalone plans
// page only answers "not for you".
//
// `billingTo` exists so a link inside a console lands inside that console: the
// default is the standalone plans page, and the Gestione Workspace caller passes
// its own Piani tab.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { COLORS, RADII, SEMANTIC } from '../../constants/theme.js';

export default function SeatTile({ seats, showBillingLink = true, billingTo = '/billing' }) {
  const { t } = useTranslation('admin');

  const seatsKnown = seats?.used != null;
  if (!seatsKnown) return null;

  const unlimitedSeats = seats.source === 'unlimited';
  const hasCap = seats.limit != null;
  const atCap = hasCap && seats.used >= seats.limit;

  return (
    <div style={{ padding: '16px 24px 0' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
        padding: '12px 16px', borderRadius: RADII.control,
        background: atCap ? SEMANTIC.attenzione.bg : COLORS.field,
        border: `1px solid ${atCap ? `${SEMANTIC.attenzione.fg}33` : COLORS.border}`,
      }}>
        <span style={{
          fontSize: '11px', fontWeight: 600, letterSpacing: '0.6px',
          textTransform: 'uppercase', color: COLORS.textSecondary,
        }}>
          {t('seats.label')}
        </span>
        <span style={{
          fontSize: '13px', fontWeight: 600,
          color: atCap ? SEMANTIC.attenzione.fg : COLORS.ink,
        }}>
          {hasCap
            ? t('seats.usedOfCap', { used: seats.used, cap: seats.limit })
            : t('seats.usedNoCap', { used: seats.used })}
        </span>
        {unlimitedSeats && (
          <span style={{ fontSize: '12px', color: COLORS.textTertiary }}>
            {t('seats.noCap')}
          </span>
        )}
        {atCap && (
          <span style={{ fontSize: '12px', fontWeight: 700, color: SEMANTIC.attenzione.fg }}>
            {t('seats.full')}
          </span>
        )}
        {/* The remedy follows `source`, never the numbers: 'customer' is a
            ceiling an operator set for the company, so offering an upgrade
            there would send the admin to buy something that changes
            nothing. An unexpected source gets NO remedy rather than a
            plausible wrong one. */}
        {atCap && seats.source === 'customer' && (
          <span style={{ fontSize: '12px', color: SEMANTIC.attenzione.fg }}>
            {t('seats.customerLimit')}
          </span>
        )}
        {atCap && seats.source === 'plan' && (
          <>
            <span style={{ fontSize: '12px', color: SEMANTIC.attenzione.fg }}>
              {t('seats.upgradeCta')}
            </span>
            {showBillingLink && (
              <Link
                to={billingTo}
                style={{ fontSize: '12px', fontWeight: 700, color: COLORS.accent, textDecoration: 'underline' }}
              >
                {t('seats.upgradeLink')}
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
